import { Injectable } from '@angular/core';
import { DISMISS_COPILOT_NOTIFICATIONS_TOOL } from '@unthread-io/copilot-frontend-tools';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

import { CopilotContextPayload, CopilotContextService, CopilotPageContextProvider } from './copilot-context.service';

/**
 * The notifications a Tailor request put in front of the model — exactly
 * what that turn shows the user, and so exactly what it may mark seen.
 */
export function presentedNotificationIds(contexts: readonly CopilotContextPayload[]): string[] {
  return contexts.flatMap((context) => {
    const notifications = (context as { notifications?: Array<{ id?: unknown }> }).notifications;

    return Array.isArray(notifications)
      ? notifications.map((notification) => notification.id).filter((id): id is string => typeof id === 'string')
      : [];
  });
}

/** A notification Copilot raised on a page, while that page is open. */
export interface CopilotPageNotification {
  /** The stored notification's id, or a local one when nothing is stored. */
  id: string;
  type: string;
  raisedAt: string;
  payload: unknown;
  /** Whether a stored notification backs it (false: it lives only in this page, e.g. a playbook never saved). */
  stored: boolean;
  seen: boolean;
  dismissed: boolean;
  dismissalNote: string | null;
}

/**
 * The notifications Copilot raised on open pages: what the Tailor button's
 * badge counts and what Tailor reads when it is opened.
 *
 * Page-scoped and in memory only. A page's notifications belong to its
 * context provider and go when it does — leaving the page, or saving and
 * being sent back to a list, ends them, and nothing brings them back later.
 * A page Ionic keeps alive while the user steps forward to another one has
 * not gone: it keeps its unsaved edits, so it keeps the notifications about
 * them, out of the badge until it is back in view.
 *
 * Pages never call this service. It hears from the copilot context service:
 * a page's content changing re-reads what still holds, a page going away
 * ends its notifications, and the unseen ones ride along in the page's
 * context to Tailor, which dismisses them through a generic action.
 *
 * The badge counts notification TYPES with anything unseen on the pages in
 * view, not notifications: several findings of one kind read as one thing to
 * look at.
 */
@Injectable({ providedIn: 'root' })
export class CopilotNotificationsService {
  private readonly byOwner = new Map<CopilotPageContextProvider, CopilotPageNotification[]>();
  private readonly changes = new BehaviorSubject<void>(undefined);

  readonly badgeCount$: Observable<number>;
  /** Unseen notifications on the pages in view, as they change. */
  readonly unseenInView$: Observable<CopilotPageNotification[]>;

  constructor(private copilotContext: CopilotContextService) {
    this.unseenInView$ = combineLatest([this.changes, this.copilotContext.activeProviders$]).pipe(
      map(() => this.unseenInView()),
    );
    this.badgeCount$ = this.unseenInView$.pipe(
      map((unseen) => new Set(unseen.map((notification) => notification.type)).size),
    );

    // Root service: these live as long as the app.
    this.copilotContext.providerUnregistered$.subscribe((owner) => this.clear(owner));
    this.copilotContext.contentChanges$.subscribe(() => this.changes.next());
    this.copilotContext.registerContextAugmenter((owner, context) => {
      const unseen = this.unseen(owner);

      return unseen.length
        ? ({
            ...context,
            notifications: unseen.map((notification) => ({
              id: notification.id,
              type: notification.type,
              raisedAt: notification.raisedAt,
              stored: notification.stored,
              payload: this.currentPayload(owner, notification),
            })),
          } as CopilotContextPayload)
        : context;
    });
    this.copilotContext.registerAppAction(DISMISS_COPILOT_NOTIFICATIONS_TOOL.name, (payload) =>
      this.dismissForTailor(payload),
    );
  }

  raise(
    owner: CopilotPageContextProvider,
    notification: Omit<CopilotPageNotification, 'seen' | 'dismissed' | 'dismissalNote'>,
  ) {
    const notifications = this.byOwner.get(owner) ?? [];

    this.byOwner.set(owner, [
      ...notifications,
      { ...notification, seen: false, dismissed: false, dismissalNote: null },
    ]);
    this.changes.next();
  }

  /** Every notification raised on this page this visit, newest first. */
  all(owner: CopilotPageContextProvider): CopilotPageNotification[] {
    return [...(this.byOwner.get(owner) ?? [])].reverse();
  }

  /** What the user has not been shown yet on this page and still applies, oldest first. */
  unseen(owner: CopilotPageContextProvider): CopilotPageNotification[] {
    return (this.byOwner.get(owner) ?? []).filter(
      (notification) =>
        !notification.seen && !notification.dismissed && this.currentPayload(owner, notification) !== null,
    );
  }

  /** Unseen notifications on every page in view. */
  unseenInView(): CopilotPageNotification[] {
    return [...this.byOwner.keys()]
      .filter((owner) => this.copilotContext.isProviderActive(owner))
      .flatMap((owner) => this.unseen(owner));
  }

  /** Marks exactly these as seen — never everything up to now, so one raised mid-read stays unseen. */
  markSeen(ids: readonly string[]) {
    this.update(ids, (notification) => (notification.seen ? notification : { ...notification, seen: true }));
  }

  dismiss(ids: readonly string[], note?: string | null) {
    this.update(ids, (notification) =>
      notification.dismissed
        ? notification
        : { ...notification, seen: true, dismissed: true, dismissalNote: note?.trim() || null },
    );
  }

  private clear(owner: CopilotPageContextProvider) {
    if (this.byOwner.delete(owner)) {
      this.changes.next();
    }
  }

  /** What of the notification still holds, by the page's own judgment; as raised when the page has none. */
  private currentPayload(owner: CopilotPageContextProvider, notification: CopilotPageNotification): unknown | null {
    return owner.currentNotificationPayload ? owner.currentNotificationPayload(notification) : notification.payload;
  }

  private dismissForTailor(payload: { ids?: unknown; note?: unknown } | null): boolean {
    const ids = Array.isArray(payload?.ids) ? payload.ids.filter((id): id is string => typeof id === 'string') : [];

    if (!ids.length) {
      return false;
    }

    this.dismiss(ids, typeof payload?.note === 'string' ? payload.note : null);

    return true;
  }

  private update(ids: readonly string[], change: (notification: CopilotPageNotification) => CopilotPageNotification) {
    const wanted = new Set(ids);

    for (const [owner, notifications] of this.byOwner) {
      this.byOwner.set(
        owner,
        notifications.map((notification) => (wanted.has(notification.id) ? change(notification) : notification)),
      );
    }

    this.changes.next();
  }
}
